import { storyIssueRulesDefaults } from '../../../lib/s4/defaults';
import type { StoryIssueRulesDef } from '../../../lib/s4/types';
import { withDefaults } from '../../../lib/teamsDefaults';
import { CheckboxField, Fieldset, NumberField } from '../fields';
import type { FormProps } from '../types';

/** `storyRules` (shared-cs `StoryIssueRulesDef`, ADR 0042): off by default; absent fields show the C# defaults. */
export function StoryIssueRulesForm({ value, onChange, errors }: FormProps<StoryIssueRulesDef>) {
  const r = withDefaults(storyIssueRulesDefaults(), value ?? {});
  const set = (patch: Partial<StoryIssueRulesDef>) => onChange({ ...value, ...patch });
  const num = (
    k: Exclude<keyof StoryIssueRulesDef, 'enabled' | 'playAnywhere'>,
    label: string,
    hint: string,
    integer = false,
  ) => (
    <NumberField
      label={label}
      hint={hint}
      integer={integer}
      min={0}
      value={r[k]}
      issues={errors[k]}
      onChange={(v) => set({ [k]: v ?? 0 })}
    />
  );
  return (
    <>
      <Fieldset legend="Story issues">
        <CheckboxField
          label="Enabled"
          hint="Off: the shelf is empty and nothing opens (no app release needed)."
          checked={!!r.enabled}
          onChange={(on) => set({ enabled: on })}
        />
        <CheckboxField
          label="Play anywhere (MISSIONS)"
          hint="ADR 0062: an issue starts from the MISSIONS screen wherever the player is; at a matching place it pays the issue's on-location bonus. Off: issues open only in person from the comics screen."
          checked={!!r.playAnywhere}
          onChange={(on) => set({ playAnywhere: on })}
        />
        {num('ticketHours', 'Ticket hours', 'A story ticket lives this long (rewinds take time).')}
        {num(
          'shortCutsceneAfter',
          'Short cutscene after',
          'Misreadings in an issue before the reader cutscene shortens to cells 1 and 4.',
          true,
        )}
      </Fieldset>
      <Fieldset legend="Where issues are played">
        {num('fallbackSearchKm', 'Fallback search km', 'No match within this → a story drop.')}
        {num(
          'storyDropKm',
          'Story drop km',
          'A story drop sits at most this far away, on a safe public spot.',
        )}
        {num('storyDropDays', 'Story drop days', "A story drop's lifetime.")}
        {num(
          'sameAsFallbackKm',
          'sameAs fallback km',
          "Farther than this from the bound building, any building matching the earlier issue's rule.",
        )}
        {num(
          'pickedWithinKm',
          'Picked within km',
          'picked: the search radius around home (or the player).',
        )}
        {num('signsHours', 'Signs hours', "How long a picked building's signs show on the map.")}
      </Fieldset>
    </>
  );
}
